import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { 
  MapPin, AlertTriangle, TrendingUp, Wrench, FileText, 
  Calendar, User, ChevronRight, ExternalLink 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface FrictionPoint {
  id: string;
  task_name: string | null;
  location: string;
  shift: string | null;
  hazard_category: string;
  primary_cause: string | null;
  report_count: number;
  friction_score: number;
  suggested_fix: string | null;
  estimated_impact: 'high' | 'medium' | 'low' | null;
  site_id?: string | null;
}

interface Report {
  id: string;
  description: string;
  created_at: string;
  risk_rating: string;
  verification_status: string;
  reporter?: { display_name: string } | null;
}

interface FrictionPointDetailDialogProps {
  point: FrictionPoint | null;
  isOpen: boolean;
  onClose: () => void;
}

export const FrictionPointDetailDialog = ({ point, isOpen, onClose }: FrictionPointDetailDialogProps) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (point && isOpen) {
      fetchRelatedReports();
    }
  }, [point, isOpen]);

  const fetchRelatedReports = async () => {
    if (!point) return;
    setIsLoading(true);
    try {
      let query = supabase
        .from('peer_reports')
        .select('id, description, created_at, risk_rating, verification_status, reporter:reporter_id(display_name)')
        .eq('location', point.location)
        .eq('hazard_category', point.hazard_category)
        .order('created_at', { ascending: false })
        .limit(10);

      if (point.site_id) {
        query = query.eq('site_id', point.site_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setReports(data as any[] || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!point) return null;

  const getImpactColor = (impact: string | null) => {
    switch (impact) {
      case 'high': return 'bg-destructive text-destructive-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'very_high': return 'text-destructive';
      case 'high': return 'text-destructive/80';
      case 'medium': return 'text-warning';
      default: return 'text-success';
    }
  };

  // Score breakdown
  const scoreBreakdown = [
    { label: 'Report Frequency', value: Math.min(point.report_count * 10, 40), max: 40 },
    { label: 'Recency Weight', value: 30, max: 30 },
    { label: 'Severity Factor', value: point.estimated_impact === 'high' ? 30 : point.estimated_impact === 'medium' ? 20 : 10, max: 30 },
  ];

  return (
<Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Friction Point Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Card */}
          <Card className={cn(
            "border-l-4",
            point.estimated_impact === 'high' ? 'border-l-destructive' :
            point.estimated_impact === 'medium' ? 'border-l-warning' : 'border-l-success'
          )}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold">{point.location}</h3>
                  <p className="text-muted-foreground">{point.hazard_category.replace(/_/g, ' ')}</p>
                </div>
                <Badge className={getImpactColor(point.estimated_impact)}>
                  {point.estimated_impact?.toUpperCase() || 'UNKNOWN'} IMPACT
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-muted/50 rounded-lg p-3">
                  <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">{point.report_count}</p>
                  <p className="text-xs text-muted-foreground">Reports</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <TrendingUp className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">{point.friction_score.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">Friction Score</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <Calendar className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">90</p>
                  <p className="text-xs text-muted-foreground">Day Window</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Breakdown */}
          <div className="space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Score Breakdown
            </h4>
            {scoreBreakdown.map((item) => (
              <div key={item.label} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium">{item.value}/{item.max}</span>
                </div>
                <Progress value={(item.value / item.max) * 100} className="h-2" />
              </div>
            ))}
          </div>

          {/* Root Cause & Fix */}
          {(point.primary_cause || point.suggested_fix) && (
            <div className="space-y-3">
              {point.primary_cause && (
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">Primary Root Cause</p>
                  <p className="text-sm">{point.primary_cause}</p>
                </div>
              )}
              {point.suggested_fix && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Wrench className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-primary mb-1">Recommended Action</p>
                      <p className="text-sm">{point.suggested_fix}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Related Reports */}
          <div className="space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Contributing Reports ({reports.length})
            </h4>
            
            {isLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading reports...</div>
            ) : reports.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">No reports found</div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {reports.map((report) => (
                  <div 
                    key={report.id} 
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{report.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{format(new Date(report.created_at), 'dd MMM yyyy')}</span>
                        {report.reporter && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {(report.reporter as any)?.display_name || 'Unknown'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Badge variant="outline" className={cn("text-xs", getRiskColor(report.risk_rating))}>
                        {report.risk_rating.replace(/_/g, ' ')}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
            <Button className="flex-1">
              <ExternalLink className="h-4 w-4 mr-2" />
              View All Reports
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FrictionPointDetailDialog;
